import { Form } from 'antd';
import { useCallback, useRef, useState } from 'react';

import {
  useCreateCertificateMutation,
  useEditCertificateMutation,
  useLazyGetCertificateByIdQuery,
} from '@app/api/Certificates';
import { FRONT_WARNING, SUCCESS, WARNING } from '@app/constants/alerts';
import { useUserPrint } from '@app/hooks';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { handleCatchError } from '@app/services';

import { buildCertificateData } from '../utils/buildCertificateData';

import type { CertificateFormValues } from '../types';

interface UseCertificateModalProps {
  isOpen: boolean;
  /** ID редактируемого сертификата, null — создание нового */
  certificateId: number | null;
  onClose: () => void;
}

export const useCertificateModal = ({
  isOpen,
  certificateId,
  onClose,
}: UseCertificateModalProps) => {
  const {
    params: { ActiveBranchId },
  } = useUserPrint();
  const { showNotification } = useNotification();

  const [form] = Form.useForm<CertificateFormValues>();
  // ID сертификата, которым заполнена форма. data ленивого запроса не подходит:
  // модалка не размонтируется, и там остаётся ответ прошлого открытия.
  const [filledCertificateId, setFilledCertificateId] = useState<number | null>(
    null
  );
  // Номер последней загрузки — ответы прошлых загрузок игнорируются
  const loadRequestRef = useRef(0);

  const isEditing = certificateId !== null;

  const [getCertificateById] = useLazyGetCertificateByIdQuery();
  const [createCertificate, { isLoading: isCreating }] =
    useCreateCertificateMutation();
  const [editCertificate, { isLoading: isEditingSaving }] =
    useEditCertificateMutation();

  const isCertificateLoading =
    isEditing && filledCertificateId !== certificateId;

  // CustomInputNumber не принимает value/onChange от Form.Item, поэтому
  // значение передаётся в него явно и записывается в форму из onSuccess
  const costInputValue = Form.useWatch('Cost', form);

  const changeCost = useCallback(
    async ({ value }: { value: string }) => {
      form.setFieldValue('Cost', value);

      // setFieldValue не валидирует поле — без этого не снимется ошибка
      // обязательного поля после ввода
      try {
        await form.validateFields(['Cost']);
      } catch {
        // Ошибка уже показана под полем
      }
    },
    [form]
  );

  const close = useCallback(() => {
    loadRequestRef.current += 1;
    form.resetFields();
    setFilledCertificateId(null);
    onClose();
  }, [form, onClose]);

  // Если сертификат не загрузился, редактировать нечего: показываем ошибку
  // и закрываем модалку.
  const loadCertificate = useCallback(async () => {
    if (!ActiveBranchId || !isOpen || certificateId === null) return;

    loadRequestRef.current += 1;
    const requestId = loadRequestRef.current;
    setFilledCertificateId(null);

    try {
      const response = await getCertificateById({
        ActiveBranchId,
        certificateId,
      }).unwrap();

      if (requestId !== loadRequestRef.current) return;

      form.setFieldsValue({
        Name: response.Name ?? '',
        Cost: String(response.Cost),
      });
      setFilledCertificateId(response.Id);
    } catch (error) {
      if (requestId !== loadRequestRef.current) return;

      showNotification(handleCatchError(error), WARNING);
      close();
    }
  }, [
    ActiveBranchId,
    certificateId,
    close,
    form,
    getCertificateById,
    isOpen,
    showNotification,
  ]);

  const submit = useCallback(async () => {
    if (!ActiveBranchId) {
      showNotification('Филиал не был распознан', FRONT_WARNING);
      return;
    }

    let values: CertificateFormValues;

    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    const data = buildCertificateData({
      values,
      certificateId,
      branchId: ActiveBranchId,
    });

    try {
      if (isEditing) {
        await editCertificate({ ActiveBranchId, data }).unwrap();
        showNotification('Сертификат сохранён', SUCCESS);
      } else {
        await createCertificate({ ActiveBranchId, data }).unwrap();
        showNotification('Сертификат создан', SUCCESS);
      }

      close();
    } catch (error) {
      showNotification(handleCatchError(error), WARNING);
    }
  }, [
    ActiveBranchId,
    certificateId,
    close,
    createCertificate,
    editCertificate,
    form,
    isEditing,
    showNotification,
  ]);

  return {
    form,
    isEditing,
    isCertificateLoading,
    isSubmitting: isCreating || isEditingSaving,
    costInputValue,
    changeCost,
    loadCertificate,
    close,
    submit,
  };
};
